import TextField from "@mui/material/TextField";
import { useCallback, useEffect, useRef, useState } from "react";

import { parseClockTime } from "~/lib/routine";

/** How long the picker has to stay still before its value counts as chosen. */
const SETTLE_MS = 800;

/**
 * A reminder's time, saved once the member has settled on it.
 *
 * A native time picker reports every notch of its wheels as a change, and saving each
 * one would put a request and a "saved" toast behind every notch. So the edit is held
 * as a draft and committed when it has been still for a moment, when the field loses
 * focus, or when the screen closes with an edit pending — whichever comes first.
 *
 * The draft is shown until the save has come back, then handed back to the store. A
 * failed save therefore snaps the field back to the time that is actually stored, the
 * same rule the switches beside it follow.
 */
export function ReminderTimeField({
  label,
  value,
  onCommit,
  testId,
}: {
  label: string;
  /** The stored time, `HH:MM`. */
  value: string;
  onCommit: (time: string) => Promise<void>;
  testId?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const latest = useRef({ draft, value, onCommit });
  const inFlight = useRef<string | null>(null);

  useEffect(() => {
    latest.current = { draft, value, onCommit };
  });

  const commit = useCallback(async () => {
    const { draft: pending, value: stored, onCommit: save } = latest.current;
    if (pending === null || pending === inFlight.current) return;
    if (pending === stored || !parseClockTime(pending)) {
      setDraft(null);
      return;
    }
    inFlight.current = pending;
    await save(pending);
    inFlight.current = null;
    // Back to the store — unless the wheel has moved again in the meantime.
    setDraft((current) => (current === pending ? null : current));
  }, []);

  useEffect(() => {
    if (draft === null) return;
    const timer = window.setTimeout(() => void commit(), SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, commit]);

  // Leaving the screen mid-edit still keeps the edit.
  useEffect(() => () => void commit(), [commit]);

  return (
    <TextField
      type="time"
      label={label}
      value={draft ?? value}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void commit()}
      slotProps={{
        inputLabel: { shrink: true },
        // Minutes, not seconds: the reminder is set to the minute on both ends.
        htmlInput: { step: 60, "data-testid": testId },
      }}
      sx={{ maxWidth: 200 }}
    />
  );
}
